import { MetaApiResponse } from '@/utils/types/CommonApiResponse';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  checkHostSmsImportability,
  createA2pBrand,
  createA2pCampaign,
  createHostSms,
  createHostSmsImportOrder,
  deleteA2pCampaign,
  deleteHostSms,
  getA2pBrands,
  getA2pCampaignLinkableNumbers,
  getA2pCampaignPricing,
  getA2pCampaigns,
  getBusinessPhoneNumbers,
  getHostSms,
  getHostSmsList,
  linkA2pCampaignNumbers,
  resendHostSmsOtp,
  updateA2pBrand,
  updateA2pCampaign,
  uploadHostSmsLoa,
  verifyHostSms
} from './api';
import {
  A2pBrand,
  A2pBrandPayload,
  A2pCampaign,
  A2pCampaignLinkPayload,
  A2pCampaignPayload,
  A2pLinkableNumber,
  HostSms,
  HostSmsCreatePayload,
  HostSmsOrderPayload,
  PhoneNumberPurchase
} from './types';
import { queryClient } from 'providers/AllProviders';

type PhoneNumbersQueryData = { data: PhoneNumberPurchase[]; meta: MetaApiResponse };
type HostSmsQueryData = { data: HostSms[]; meta: MetaApiResponse };
type A2pBrandsQueryData = { data: A2pBrand[]; meta: MetaApiResponse };
type A2pCampaignsQueryData = { data: A2pCampaign[]; meta: MetaApiResponse };
type PhoneNumbersQueryOptions = Omit<
  UseQueryOptions<PhoneNumbersQueryData, Error, PhoneNumbersQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;
type HostSmsQueryOptions = Omit<
  UseQueryOptions<HostSmsQueryData, Error, HostSmsQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;
type A2pBrandsQueryOptions = Omit<
  UseQueryOptions<A2pBrandsQueryData, Error, A2pBrandsQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;
type A2pCampaignsQueryOptions = Omit<
  UseQueryOptions<A2pCampaignsQueryData, Error, A2pCampaignsQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;
type A2pLinkableNumbersQueryData = { data: A2pLinkableNumber[] };
type A2pLinkableNumbersQueryOptions = Omit<
  UseQueryOptions<A2pLinkableNumbersQueryData, Error, A2pLinkableNumbersQueryData, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;

export function useGetBusinessPhoneNumbers(params?: Record<string, unknown>, options?: PhoneNumbersQueryOptions) {
  return useQuery<PhoneNumbersQueryData>({
    queryKey: ['business_phone_numbers', params],
    queryFn: () => getBusinessPhoneNumbers(params),
    ...options
  });
}

export function useGetHostSmsList(params?: Record<string, unknown>, options?: HostSmsQueryOptions) {
  return useQuery<HostSmsQueryData>({
    queryKey: ['host_sms', params],
    queryFn: () => getHostSmsList(params),
    ...options
  });
}

type HostSmsDetailOptions = Omit<UseQueryOptions<HostSms, Error, HostSms, readonly unknown[]>, 'queryKey' | 'queryFn'>;

export function useGetHostSms(id?: number | string | null, options?: HostSmsDetailOptions) {
  return useQuery<HostSms>({
    queryKey: ['host_sms', 'detail', id],
    queryFn: () => getHostSms(id as number | string),
    enabled: Boolean(id),
    ...options
  });
}

export function useCreateHostSms() {
  return useMutation({
    mutationFn: (payload: HostSmsCreatePayload) => createHostSms(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
      queryClient.invalidateQueries({ queryKey: ['telephony_endpoints'] });
      queryClient.invalidateQueries({ queryKey: ['phone_numbers'] });
    }
  });
}

export function useResendHostSmsOtp() {
  return useMutation({
    mutationFn: (id: number | string) => resendHostSmsOtp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
    }
  });
}

export function useVerifyHostSms() {
  return useMutation({
    mutationFn: ({ id, otp }: { id: number | string; otp: string }) => verifyHostSms(id, { otp }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
    }
  });
}

export function useCheckHostSmsImportability() {
  return useMutation({
    mutationFn: (payload: { host_sms: { number: string } }) => checkHostSmsImportability(payload)
  });
}

export function useCreateHostSmsImportOrder() {
  return useMutation({
    mutationFn: ({ id, payload, loa }: { id: number | string; payload: HostSmsOrderPayload; loa?: File | null }) =>
      createHostSmsImportOrder(id, payload, loa),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
    }
  });
}

export function useUploadHostSmsLoa() {
  return useMutation({
    mutationFn: ({ id, file }: { id: number | string; file: File }) => uploadHostSmsLoa(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
    }
  });
}

export function useDeleteHostSms() {
  return useMutation({
    mutationFn: (id: number | string) => deleteHostSms(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
      queryClient.invalidateQueries({ queryKey: ['business_phone_numbers'] });
    }
  });
}

export function useGetA2pBrands(params?: Record<string, unknown>, options?: A2pBrandsQueryOptions) {
  return useQuery<A2pBrandsQueryData>({
    queryKey: ['a2p_brands', params],
    queryFn: () => getA2pBrands(params),
    ...options
  });
}

export function useCreateA2pBrand() {
  return useMutation({
    mutationFn: (payload: { a2p_brand: A2pBrandPayload }) => createA2pBrand(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_brands'] });
    }
  });
}

export function useUpdateA2pBrand() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: { a2p_brand: A2pBrandPayload } }) =>
      updateA2pBrand(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_brands'] });
    }
  });
}

export function useGetA2pCampaigns(params?: Record<string, unknown>, options?: A2pCampaignsQueryOptions) {
  return useQuery<A2pCampaignsQueryData>({
    queryKey: ['a2p_campaigns', params],
    queryFn: () => getA2pCampaigns(params),
    ...options
  });
}

export function useCreateA2pCampaign() {
  return useMutation({
    mutationFn: (payload: { a2p_campaign: A2pCampaignPayload }) => createA2pCampaign(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_campaigns'] });
    }
  });
}

export function useUpdateA2pCampaign() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: { a2p_campaign: A2pCampaignPayload } }) =>
      updateA2pCampaign(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_campaigns'] });
    }
  });
}

export function useDeleteA2pCampaign() {
  return useMutation({
    mutationFn: (id: number | string) => deleteA2pCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
      queryClient.invalidateQueries({ queryKey: ['business_phone_numbers'] });
    }
  });
}

export function useGetA2pCampaignLinkableNumbers(
  id?: number | string | null,
  options?: A2pLinkableNumbersQueryOptions
) {
  return useQuery<A2pLinkableNumbersQueryData>({
    queryKey: ['a2p_campaigns', 'linkable_numbers', id],
    queryFn: () => getA2pCampaignLinkableNumbers(id as number | string),
    enabled: Boolean(id),
    ...options
  });
}

export function useGetA2pCampaignPricing(enabled = true) {
  return useQuery({
    queryKey: ['a2p_campaigns', 'pricing'],
    queryFn: getA2pCampaignPricing,
    enabled
  });
}

export function useLinkA2pCampaignNumbers() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: A2pCampaignLinkPayload }) =>
      linkA2pCampaignNumbers(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['a2p_campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['host_sms'] });
      queryClient.invalidateQueries({ queryKey: ['business_phone_numbers'] });
      queryClient.invalidateQueries({ queryKey: ['telephony_endpoints'] });
    }
  });
}
